import { __, compose, curry, is, length, match, path, replace, when, tap } from 'ramda'
import service from './dictionary/service.json'
import strategy from './dictionary/strategy.json'

const pathFunc = path
const matchPath = curry((pattern, path) => compose(Boolean, length, match(__, path))(pattern))
const isString = is(String)

/**
 * Define the cache pools available for the application. The pools must be defined as an object with the following keys:
 *
 * 'pattern' => The regex pattern the path must match for the pool to activate for the request.
 * 'strategy' => The storage strategy to implement. See dictionary/strategy.json
 * 'timeToLive' => The lifespan of the cache objects in seconds
 * 'service' => The service to use for reading and writing cache entries. See dictionary/services.json
 * 'serviceArgs' => A list of arguments passed to the service so that it may operate.
 */
export default {
  offer: {
    test: matchPath(/^\/yoda\/api\/offers(\/)?(\d+)*(\/)?$/),
    strategy: strategy.volatile,
    timeToLive: 7200, // 2 hours
    service: service.item,
    serviceArgs: {
      requestIdProvider: replace('/yoda', ''),
      entityIdProvider: path(['id']),
    },
  },
  offerLine: {
    test(path, parameters) {
      if (!path.startsWith('/yoda/api/offer-lines')) {
        return false
      }

      const offerParameter = compose(Boolean, Number, pathFunc(['offer']))(parameters)
      const idParameter = compose(is(Array), pathFunc(['id']))(parameters)

      // Check for an index request
      if (matchPath(/^\/yoda\/api\/offer-lines(\/)?$/, path) && (offerParameter || idParameter)) {
        return true
      }

      // Check for a fetch request (these are now fairly rare)
      return !!(matchPath(/^\/yoda\/api\/offer-lines\/\d+(\/)?$/, path) && !offerParameter && !idParameter)
    },
    pattern: /^\/yoda\/api\/offer-lines/,
    strategy: strategy.localStorage,
    timeToLive: 1209600, // 2 weeks
    service: service.group,
    serviceArgs: {
      isGroupRequest(path, parameters) {
        return Boolean(pathFunc(['offer'], parameters))
      },
      requestIdProvider(path, parameters) {
        let id = pathFunc(['offer'], parameters)
        if (!id && matchPath(/^\/yoda\/api\/offer-lines\/\d+(\/)?$/, path)) {
          id = replace('/yoda', '', path)
          if (id.substr(id.length - 1) === '/') {
            id = id.substr(0, id.length - 1)
          }
        }
        return id ? String(id) : null
      },
      entityIdProvider: path(['id']),
      groupIdProvider: compose(
        when(isString, compose(
          replace('/', ''),
          replace('/api/offers', ''),
        )),
        path(['attributes', 'offer']),
      ),
    },
  },
}
